import { defineStore } from 'pinia'

import { computed, ref, watch } from 'vue'

import {
  DrawerState,
  LeftDrawerContent,
  RightDrawerContent,
} from '../types/drawer'

import { persistDrawerState } from '../api/drawer'

const defaultInitialState: DrawerState = {
  content: '',
  direction: 'initial',
}

export const useDrawer = defineStore('drawer', () => {
  const open = ref<DrawerState>(defaultInitialState)
  const shouldPersistState = ref<boolean>(true)

  const isLeftDrawerOpen = computed(() => open.value.direction === 'left')
  const isRightDrawerOpen = computed(() => open.value.direction === 'right')

  const isAnyDrawerOpen = computed(
    () => isLeftDrawerOpen.value || isRightDrawerOpen.value
  )

  const isOpenDirty = computed(
    () => open.value && open.value.direction !== 'initial'
  )

  function openDrawer(drawer: DrawerState | undefined): void {
    open.value = drawer || defaultInitialState
  }

  function openLeftDrawerDefault(): void {
    openDrawerDefault({
      content: LeftDrawerContent.Default,
      direction: 'left',
    })
  }

  function openDrawerDefault(drawer: DrawerState | undefined): void {
    if (isOpenDirty.value) {
      return
    }

    shouldPersistState.value = false
    openDrawer(drawer)
  }

  function openLeftDrawer(): void {
    openDrawer({
      content: LeftDrawerContent.Default,
      direction: 'left',
    })
  }

  function openRightDrawer(
    content: RightDrawerContent,
    skipPersistState: boolean = false
  ): void {
    shouldPersistState.value = !skipPersistState
    openDrawer({
      content,
      direction: 'right',
    })
  }

  function toggleDrawer(drawer: DrawerState): void {
    if (
      open.value.direction === drawer.direction &&
      open.value.content === drawer.content
    ) {
      closeDrawer()
    } else {
      openDrawer(drawer)
    }
  }

  function closeDrawer(): void {
    open.value = {
      content: 'default',
      direction: 'none',
    }
  }

  function setInitialData(initialData: DrawerState): void {
    open.value = initialData
  }

  watch(open, (newValue, oldValue) => {
    if (
      newValue.direction !== oldValue.direction ||
      newValue.content !== oldValue.content
    ) {
      if (
        newValue.direction !== 'initial' &&
        newValue.content !== RightDrawerContent.FileAttachments
      ) {
        if (shouldPersistState.value) {
          void persistDrawerState(newValue)
        } else {
          // Reset the persist-state to default(true)
          // after the drawer has opened initially (not by user)
          shouldPersistState.value = true
        }
      }

      if (newValue.direction === 'none') {
        window.eventHub.$emit('drawer:close')
      }

      if (newValue.direction === 'left' || newValue.direction === 'right') {
        window.eventHub.$emit('drawer:open', newValue)
      }
    }
  })

  return {
    isAnyDrawerOpen,
    isLeftDrawerOpen,
    isRightDrawerOpen,
    open,
    openDrawer,
    openDrawerDefault,
    openLeftDrawer,
    openLeftDrawerDefault,
    openRightDrawer,
    toggleDrawer,
    closeDrawer,
    setInitialData,
  }
})
